import { request, requestWithProgress } from "./client";

// Server-side file operations (chunked-upload completion and Bunny storage
// moves) download + re-upload the file on the backend, which for multi-GB
// media legitimately takes minutes. The default 30s request timeout would
// abort calls that are succeeding.
const FILE_TRANSFER_TIMEOUT_MS = 15 * 60 * 1000;

export function fetchDreamcastMedia() {
  return request("/media");
}

export function fetchDreamcastMediaById(id) {
  return request(`/media/${id}`);
}

export function fetchDreamcastDisclosures({ publishedOnly = false } = {}) {
  const query = publishedOnly ? "?publishedOnly=true" : "";
  return request(`/disclosures${query}`);
}

export function fetchDreamcastDisclosureById(id) {
  return request(`/disclosures/${id}`);
}

export function createDreamcastDisclosure(body) {
  return request("/disclosures", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function updateDreamcastDisclosure(id, body) {
  return request(`/disclosures/${id}`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

export function fetchDreamcastMediaFilesById(id) {
  return request(`/media/${id}/files`);
}

export function fetchDreamcastContentSettingsConfig() {
  return request("/config/content-settings");
}

export function createDreamcastMedia(body) {
  return request("/media", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function updateDreamcastMedia(id, body) {
  return request(`/media/${id}`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

export function duplicateDreamcastMedia(id) {
  return request(`/media/${id}/duplicate`, {
    method: "POST",
  });
}

export function setDreamcastMediaArchiveState(
  id,
  workflowStatus,
  displayStatus,
) {
  return request(`/media/${id}/archive`, {
    method: "PATCH",
    body: JSON.stringify({ workflowStatus, displayStatus }),
  });
}

export async function uploadDreamcastThumbnailFile(file, options = {}) {
  const { destination } = options;

  let filename = file.name;
  if (destination === "profiles" || destination === "playlists") {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 10000);
    const dotIndex = file.name.lastIndexOf(".");
    const extension = dotIndex >= 0 ? file.name.slice(dotIndex) : ".jpg";
    filename = `temp_${timestamp}_${random}${extension}`;
  }

  const arrayBuffer = await file.arrayBuffer();
  const bytes = new Uint8Array(arrayBuffer);
  let binary = "";
  const chunkSize = 8192;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  const base64 = btoa(binary);

  const url = "/thumbnails/upload";
  return request(url, {
    method: "POST",
    body: JSON.stringify({
      filename,
      contentType: file.type,
      data: base64,
      destination,
    }),
  });
}

export async function uploadDreamcastSpeakerPhotoFile(
  file,
  photoType = "",
  signal,
) {
  // Generate temp filename: temp_{timestamp}_{random}{photoType}.jpg
  const timestamp = Date.now();
  const random = Math.floor(Math.random() * 10000);
  const extension = file.name.split(".").pop() || "jpg";
  const tempFilename = `temp_${timestamp}_${random}${photoType}.${extension}`;

  const arrayBuffer = await file.arrayBuffer();
  const bytes = new Uint8Array(arrayBuffer);
  let binary = "";
  const chunkSize = 8192;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  const base64 = btoa(binary);

  const url = "/thumbnails/upload";
  const response = await request(url, {
    method: "POST",
    body: JSON.stringify({
      filename: tempFilename,
      contentType: file.type,
      data: base64,
      destination: "profiles",
    }),
    signal,
  });

  // Return both URL and temp filename for tracking
  return {
    ...response,
    tempFilename,
  };
}

// The deployed API rejects any single request body over 100MB (measured on
// dev 2026-09-30: 99MB reached the function, 101MB failed with a bare 500
// before it ran). Files at or above this threshold therefore go through the
// direct-to-storage path (SAS block upload + background transfer to Bunny),
// which has no size limit; smaller files use one direct request. Kept under
// 100MB with headroom — the old 195MB threshold sent 100–195MB files down the
// direct path, where every one of them failed.
const CHUNK_UPLOAD_THRESHOLD = 90 * 1024 * 1024;
// Block size for direct-to-storage staging. Smaller than the old 40MB chunks:
// more parallelism granularity and cheaper retries.
const CHUNK_SIZE = 8 * 1024 * 1024;
// Parallel blocks multiply throughput: per-connection rates cap around
// ~1 MB/s while the same machine sustains ~4 MB/s across parallel streams.
const BLOCK_CONCURRENCY = 8;
const BLOCK_UPLOAD_MAX_ATTEMPTS = 4;
const BLOCK_UPLOAD_TIMEOUT_MS = 2 * 60 * 1000;
const RETRY_BASE_DELAY_MS = 500;
// Server-side transfer of a multi-GB upload to Bunny can take a while. The
// server reports progress and fails the job itself if its worker dies, so
// the client keeps polling while progress moves and only gives up when the
// server stops reporting anything new for ASSEMBLY_POLL_IDLE_MS, or after
// the absolute ASSEMBLY_POLL_BUDGET_MS backstop.
const ASSEMBLY_POLL_INTERVAL_MS = 5 * 1000;
const ASSEMBLY_POLL_IDLE_MS = 20 * 60 * 1000;
const ASSEMBLY_POLL_BUDGET_MS = 4 * 60 * 60 * 1000;

const isRetryableUploadStatus = (status) =>
  status === 0 || status === 408 || status === 429 || status >= 500;

const isRetryableUploadError = (error) =>
  isRetryableUploadStatus(error?.status) ||
  /network|timeout|interrupted/i.test(String(error?.message || ""));

const waitForRetry = (delayMs, signal) =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("The operation was aborted.", "AbortError"));
      return;
    }

    const timeoutId = setTimeout(() => {
      signal?.removeEventListener("abort", handleAbort);
      resolve();
    }, delayMs);
    const handleAbort = () => {
      clearTimeout(timeoutId);
      signal?.removeEventListener("abort", handleAbort);
      reject(new DOMException("The operation was aborted.", "AbortError"));
    };
    signal?.addEventListener("abort", handleAbort, { once: true });
  });

async function requestWithUploadRetry(path, options, signal) {
  for (let attempt = 1; ; attempt += 1) {
    try {
      return await request(path, { ...options, signal, timeout: FILE_TRANSFER_TIMEOUT_MS });
    } catch (error) {
      if (
        signal?.aborted ||
        error?.name === "AbortError" ||
        attempt >= BLOCK_UPLOAD_MAX_ATTEMPTS ||
        !isRetryableUploadError(error)
      ) {
        throw error;
      }
      await waitForRetry(RETRY_BASE_DELAY_MS * 2 ** (attempt - 1), signal);
    }
  }
}

async function requestWithFetchRetry(url, options, signal) {
  for (let attempt = 1; ; attempt += 1) {
    const controller = new AbortController();
    const timeoutId = setTimeout(
      () => controller.abort(),
      BLOCK_UPLOAD_TIMEOUT_MS,
    );
    const handleAbort = () => controller.abort();
    signal?.addEventListener("abort", handleAbort, { once: true });

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });
      if (response.ok) {
        return response;
      }

      const error = new Error(
        `Block list commit failed with status ${response.status}`,
      );
      error.status = response.status;
      throw error;
    } catch (error) {
      const timedOut = error?.name === "AbortError" && !signal?.aborted;
      if (
        signal?.aborted ||
        attempt >= BLOCK_UPLOAD_MAX_ATTEMPTS ||
        (!timedOut && !isRetryableUploadError(error))
      ) {
        throw error;
      }
      await waitForRetry(RETRY_BASE_DELAY_MS * 2 ** (attempt - 1), signal);
    } finally {
      clearTimeout(timeoutId);
      signal?.removeEventListener("abort", handleAbort);
    }
  }
}

async function uploadDreamcastMediaFileInChunks(
  file,
  filename,
  contentType,
  signal,
  onProgress,
) {
  // Direct-to-storage upload: ask the API for a scoped SAS URL (write access
  // to ONE scratch blob, short-lived — no credentials in the browser), then
  // stage blocks straight to Azure Blob storage in parallel. The old path
  // pushed every chunk through APIM + a Function, capping throughput at
  // ~1.1 MB/s per connection (measured) — a third of the same machine's
  // direct-to-blob rate. Bunny remains the final store: the server-side
  // assembler picks the blob up from scratch exactly as before.
  const sasResponse = await request("/media/upload/sas", {
    method: "POST",
    body: JSON.stringify({ filename }),
  });
  const uploadId = sasResponse?.data?.uploadId;
  const sasUrl = sasResponse?.data?.sasUrl;
  if (!uploadId || !sasUrl) {
    throw new Error("Failed to obtain an upload destination.");
  }

  const totalParts = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
  const bytesUploadedByPart = new Array(totalParts).fill(0);

  const reportProgress = () => {
    if (!onProgress) return;
    const totalLoaded = bytesUploadedByPart.reduce((sum, n) => sum + n, 0);
    onProgress(totalLoaded / file.size);
  };

  // Azure Block Blob REST protocol: stage each block (PUT ?comp=block), then
  // commit the ordered block list (PUT ?comp=blocklist). Same-length,
  // order-sortable base64 block ids per the API contract.
  const blockIdFor = (partNumber) =>
    btoa(String(partNumber).padStart(10, "0"));

  const stageBlockAttempt = (partNumber, chunk, url) =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      let settled = false;
      const finish = (callback) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        signal?.removeEventListener("abort", handleAbort);
        callback();
      };
      const handleAbort = () => xhr.abort();
      const timeoutId = setTimeout(() => xhr.abort(), BLOCK_UPLOAD_TIMEOUT_MS);

      xhr.open("PUT", url);
      xhr.setRequestHeader("x-ms-blob-type", "BlockBlob");
      xhr.upload.onprogress = (event) => {
        bytesUploadedByPart[partNumber] = event.loaded;
        reportProgress();
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          finish(resolve);
          return;
        }
        const error = new Error(
          `Block upload failed with status ${xhr.status}`,
        );
        error.status = xhr.status;
        finish(() => reject(error));
      };
      xhr.onerror = () => {
        const error = new Error("Block upload failed");
        error.status = xhr.status || 0;
        finish(() => reject(error));
      };
      xhr.onabort = () => {
        const error = new Error("Block upload was interrupted");
        error.status = 0;
        error.name = signal?.aborted ? "AbortError" : "NetworkError";
        finish(() => reject(error));
      };
      signal?.addEventListener("abort", handleAbort, { once: true });
      xhr.send(chunk);
    });

  const stageBlock = async (partNumber) => {
    const start = partNumber * CHUNK_SIZE;
    // Blob.slice() is a lazy view, not a copy — each block's bytes are only
    // read from disk when this request actually sends it.
    const chunk = file.slice(start, start + CHUNK_SIZE);
    const url = `${sasUrl}&comp=block&blockid=${encodeURIComponent(blockIdFor(partNumber))}`;

    for (let attempt = 1; ; attempt += 1) {
      bytesUploadedByPart[partNumber] = 0;
      reportProgress();
      try {
        await stageBlockAttempt(partNumber, chunk, url);
        bytesUploadedByPart[partNumber] = chunk.size;
        reportProgress();
        return;
      } catch (error) {
        if (
          signal?.aborted ||
          error?.name === "AbortError" ||
          attempt >= BLOCK_UPLOAD_MAX_ATTEMPTS ||
          !isRetryableUploadError(error)
        ) {
          throw error;
        }
        await waitForRetry(RETRY_BASE_DELAY_MS * 2 ** (attempt - 1), signal);
      }
    }
  };

  // Worker-pool: parallel blocks multiply throughput because per-connection
  // rates are the bottleneck, not total bandwidth (measured ~1 MB/s per
  // stream vs ~4 MB/s at concurrency 4+).
  let nextPartNumber = 0;
  const runWorker = async () => {
    while (nextPartNumber < totalParts) {
      if (signal?.aborted) {
        throw new DOMException("The operation was aborted.", "AbortError");
      }
      const partNumber = nextPartNumber;
      nextPartNumber += 1;
      await stageBlock(partNumber);
    }
  };
  const workerCount = Math.min(BLOCK_CONCURRENCY, totalParts);
  await Promise.all(Array.from({ length: workerCount }, runWorker));

  if (bytesUploadedByPart.some((bytes, index) => bytes !== file.slice(index * CHUNK_SIZE, (index + 1) * CHUNK_SIZE).size)) {
    throw new Error("Upload did not stage every byte of the file.");
  }

  // Commit the block list (makes the blob real).
  const blockListXml = `<?xml version="1.0" encoding="utf-8"?><BlockList>${Array.from(
    { length: totalParts },
    (_, i) => `<Latest>${blockIdFor(i)}</Latest>`,
  ).join("")}</BlockList>`;
  const commitResponse = await requestWithFetchRetry(
    `${sasUrl}&comp=blocklist`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/xml" },
      body: blockListXml,
    },
    signal,
  );
  if (!commitResponse.ok) {
    throw new Error(
      `Finalizing the upload failed with status ${commitResponse.status}`,
    );
  }

  // Completion is asynchronous server-side: Azure hard-caps HTTP responses at
  // ~230s, so multi-GB assemblies run in a background queue function instead
  // of inside the request (they used to 504 at the gateway). The first POST
  // enqueues the job (202); re-POSTing the same body polls until the server
  // reports 201 complete (with the original response shape) or a failure.
  const completeBody = JSON.stringify({
    uploadId,
    preCommitted: true,
    filename,
    contentType,
    fileSize: file.size,
  });
  const pollStartedAt = Date.now();
  let lastProgressKey = null;
  let lastProgressChangeAt = Date.now();
  for (;;) {
    if (signal?.aborted) {
      throw new DOMException("The operation was aborted.", "AbortError");
    }

    const response = await requestWithUploadRetry("/media/upload/complete", {
      method: "POST",
      body: completeBody,
    }, signal);

    if (response?.data?.status === "failed") {
      throw new Error(
        response.data.error || "Upload assembly failed on the server.",
      );
    }

    if (response?.data?.status !== "processing") {
      return response;
    }

    const serverProgress = response?.data?.progress;
    const progressKey = serverProgress
      ? `${serverProgress.phase}:${serverProgress.attempt}:${serverProgress.bytesSent}`
      : null;
    if (progressKey !== lastProgressKey) {
      lastProgressKey = progressKey;
      lastProgressChangeAt = Date.now();
    }
    if (onProgress && serverProgress) {
      onProgress(1, {
        phase: serverProgress.phase,
        attempt: serverProgress.attempt,
        fraction:
          serverProgress.totalBytes > 0
            ? serverProgress.bytesSent / serverProgress.totalBytes
            : null,
      });
    }

    if (
      Date.now() - lastProgressChangeAt > ASSEMBLY_POLL_IDLE_MS ||
      Date.now() - pollStartedAt > ASSEMBLY_POLL_BUDGET_MS
    ) {
      throw new Error(
        "The upload is still being processed on the server. Please try again in a few minutes.",
      );
    }

    await new Promise((resolve) =>
      setTimeout(resolve, ASSEMBLY_POLL_INTERVAL_MS),
    );
  }
}

export async function uploadDreamcastMediaFile(file, signal, onProgress) {
  const MIME_MAP = {
    ".wav": "audio/wav",
    ".mp3": "audio/mpeg",
    ".m4a": "audio/mp4",
    ".aac": "audio/aac",
    ".mp4": "video/mp4",
    ".mov": "video/quicktime",
  };
  const ext = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
  const contentType = MIME_MAP[ext] || file.type || "application/octet-stream";

  // Determine if this is an audio file that should be converted to MP3
  const audioExtensions = [".wav", ".m4a", ".aac"];
  const shouldConvertToMp3 = audioExtensions.includes(ext);
  const filename = shouldConvertToMp3
    ? file.name.replace(ext, ".mp3")
    : file.name;

  // Stream the raw file bytes directly instead of base64-encoding into a
  // JSON string — base64 encoding a large audio/video file duplicates it
  // several times over in browser memory (arrayBuffer + binary string +
  // base64 string + JSON body) and crashes the tab.
  let response;
  if (file.size > CHUNK_UPLOAD_THRESHOLD) {
    response = await uploadDreamcastMediaFileInChunks(
      file,
      filename,
      contentType,
      signal,
      onProgress,
    );
  } else {
    const params = new URLSearchParams({ filename, contentType });
    const url = `/media/upload?${params.toString()}`;
    response = await requestWithProgress(
      url,
      {
        method: "POST",
        // Always octet-stream: the Functions runtime only hands the body over
        // as an untouched Buffer for binary content types — audio/video MIME
        // types get UTF-8-decoded, corrupting every byte >= 0x80. The real
        // content type travels in the query string.
        headers: { "Content-Type": "application/octet-stream" },
        body: file,
        signal,
      },
      onProgress && ((loaded, total) => onProgress(loaded / total)),
    );
  }

  // Return response with tempFilename for tracking (may be converted format)
  return { ...response, tempFilename: response.data?.tempFilename || filename };
}

export async function uploadDreamcastSpeakerIntroFile(file, signal) {
  const MIME_MAP = {
    ".wav": "audio/wav",
    ".mp3": "audio/mpeg",
    ".m4a": "audio/mp4",
    ".aac": "audio/aac",
    ".mp4": "video/mp4",
    ".mov": "video/quicktime",
  };
  const ext = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
  const contentType = MIME_MAP[ext] || file.type || "application/octet-stream";
  const filename = file.name;

  const arrayBuffer = await file.arrayBuffer();
  const bytes = new Uint8Array(arrayBuffer);
  let binary = "";
  const chunkSize = 8192;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  const base64 = btoa(binary);

  const url = "/speakers/intros/upload";
  const response = await request(url, {
    method: "POST",
    body: JSON.stringify({ filename, contentType, data: base64 }),
    signal,
  });

  // Use tempFilename from response (which may be converted to MP3)
  return { ...response, tempFilename: response.data?.tempFilename || filename };
}

export function deleteSpeakerTempIntros(filenames) {
  return request("/speakers/delete-temp-intros", {
    method: "POST",
    body: JSON.stringify({ tempFilenames: filenames }),
  });
}

export function createDreamcastContentSettingsTagOption(field, value) {
  return request("/config/content-settings/tags", {
    method: "POST",
    body: JSON.stringify({ field, value }),
  });
}

export function deleteDreamcastContentSettingsTagOption(tagOptionId) {
  const encodedTagOptionId = encodeURIComponent(tagOptionId);

  return request(`/config/content-settings/tags/${encodedTagOptionId}`, {
    method: "DELETE",
  });
}

/**
 * Rename speaker photos from temp names to final names after speaker is created.
 * @param {number} speakerId - contributorId of the newly created speaker
 * @param {Array<{tempFilename, photoType}>} photos - array of temp photos to rename
 *   photoType: "" (individual/couple main), "_a", or "_b"
 */
export function renameSpeakerPhotos(speakerId, photos) {
  return request(`/speakers/${speakerId}/rename-photos`, {
    method: "POST",
    body: JSON.stringify({ photos }),
  });
}

export function renameSpeakerIntros(speakerId, intros) {
  return request(`/speakers/${speakerId}/rename-intros`, {
    method: "POST",
    body: JSON.stringify({ intros }),
  });
}

export function moveSpeakerTempIntros(speakerId, tempFilenames) {
  return request(`/speakers/${speakerId}/rename-intros`, {
    method: "POST",
    body: JSON.stringify({
      intros: tempFilenames.map((tempFilename) => ({ tempFilename })),
    }),
  });
}

/**
 * Delete temporary speaker photo files if user cancels creation.
 * @param {Array<string>} tempFilenames - array of temp filenames to delete
 */
export function deleteSpeakerTempPhotos(tempFilenames) {
  return request("/speakers/delete-temp-photos", {
    method: "POST",
    body: JSON.stringify({ tempFilenames }),
  });
}

/**
 * Move media audio files from temp folder to media folder structure.
 * @param {number} mediaId - mediaItemId of the media
 * @param {Array<string>} audioFilenames - array of audio filenames to move from temp to /audio_files
 */
export function moveMediaTempAudioFiles(mediaId, audioFilenames) {
  return request(`/media/${mediaId}/move-audio-files`, {
    method: "POST",
    body: JSON.stringify({ audioFilenames }),
    timeout: FILE_TRANSFER_TIMEOUT_MS,
  });
}

/**
 * Move media video files from temp folder to media folder structure.
 * @param {number} mediaId - mediaItemId of the media
 * @param {Array<string>} videoFilenames - array of video filenames to move from temp to /video_files
 */
export function moveMediaTempVideoFiles(mediaId, videoFilenames) {
  return request(`/media/${mediaId}/move-video-files`, {
    method: "POST",
    body: JSON.stringify({ videoFilenames }),
    timeout: FILE_TRANSFER_TIMEOUT_MS,
  });
}

/**
 * Move media files that are marked archived from temp into /medias/{id}/archived_files.
 * @param {number} mediaId - mediaItemId of the media
 * @param {Array<string>} archivedFilenames - array of filenames to move to archived_files
 */
export function moveMediaTempArchivedFiles(mediaId, archivedFilenames) {
  return request(`/media/${mediaId}/move-archived-files`, {
    method: "POST",
    body: JSON.stringify({ archivedFilenames }),
    timeout: FILE_TRANSFER_TIMEOUT_MS,
  });
}

/**
 * Move media thumbnail/image file from temp folder to media folder.
 * @param {number} mediaId - mediaItemId of the media
 * @param {string} tempFilename - temp filename of the image to move
 * @param {string} [oldUrl] - existing thumbnail URL to delete after move
 */
export function moveMediaTempImage(mediaId, tempFilename, oldUrl) {
  return request(`/media/${encodeURIComponent(mediaId)}/move-image`, {
    method: "POST",
    body: JSON.stringify({ tempFilename, oldUrl }),
  });
}

/**
 * Delete temporary media files if user cancels.
 * @param {Array<string>} tempFilenames - array of temp filenames to delete
 */
export function deleteMediaTempFiles(tempFilenames) {
  return request("/media/delete-temp-files", {
    method: "POST",
    body: JSON.stringify({ tempFilenames }),
  });
}

/**
 * Delete persisted media files from Bunny storage for a specific media item.
 * @param {number} mediaId
 * @param {Array<string>} fileUrls
 */
export function deleteMediaFiles(mediaId, fileUrls) {
  return request(`/media/${mediaId}/delete-files`, {
    method: "POST",
    body: JSON.stringify({ fileUrls }),
  });
}

/**
 * Move media file from temp location to active location.
 * If media already has an active file, the old one is moved to archived folder.
 * @param {number} mediaId - ID of the media
 * @param {string} tempFilename - temp filename to move to active
 * @param {string} [oldActiveFilename] - current active filename (will be moved to archived)
 */
export function moveMediaActiveFile(mediaId, tempFilename, oldActiveFilename) {
  return runMediaFileOperation(`/media/${mediaId}/move-active-file`, {
    tempFilename,
    oldActiveFilename,
  });
}

// Moving a multi-GB file on Bunny outlasts the gateway's HTTP timeout, so the
// API runs moves as background jobs: the first POST (with an operationId)
// starts the job and returns "processing"; re-POSTing the same body polls
// until the job returns its result or fails. Older APIs ignore operationId
// and answer the first POST directly, which this also handles.
const FILE_OPERATION_POLL_INTERVAL_MS = 3 * 1000;
const FILE_OPERATION_POLL_BUDGET_MS = 60 * 60 * 1000;

const newOperationId = () =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `op-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;

async function runMediaFileOperation(path, payload) {
  const body = JSON.stringify({ ...payload, operationId: newOperationId() });
  const startedAt = Date.now();
  for (;;) {
    const response = await request(path, {
      method: "POST",
      body,
      timeout: 60 * 1000,
    });
    if (response?.data?.status !== "processing") {
      return response;
    }
    if (Date.now() - startedAt > FILE_OPERATION_POLL_BUDGET_MS) {
      throw new Error(
        "The file is still being moved on the server. Please refresh in a few minutes.",
      );
    }
    await new Promise((resolve) =>
      setTimeout(resolve, FILE_OPERATION_POLL_INTERVAL_MS),
    );
  }
}

/**
 * Restore a media file from archive, moving the current active file to archive.
 * @param {number} mediaId - ID of the media
 * @param {string} archivedFilename - filename to restore from archive
 * @param {string} currentActiveFilename - current active filename (will be moved to archived)
 */
export function restoreMediaArchivedFile(
  mediaId,
  archivedFilename,
  currentActiveFilename,
) {
  return runMediaFileOperation(`/media/${mediaId}/restore-archived-file`, {
    archivedFilename,
    currentActiveFilename,
  });
}
